// DagBranchPanel — the BRANCHING views from the /dag_design prototype, ported to
// the unified Thread-V5 DAG explorer. It realises the three branch sub-views the
// trimmed core dropped:
//
//   1. The git/commit GRAPH — branches as lanes, commits as dots, parent edges as
//      lane-hopping curves, merged-vs-unmerged styling, the current HEAD ringed,
//      and tags ("only in coarse"). Ported from the prototype's gitTree()/gitView.
//   2. Branch COMPARE — pick two commits (A / B); the diff readout shows their
//      relation (ancestor vs diverged), the shared base, and the per-side unique
//      counts. Ported from gitDiff().
//   3. MERGE PREVIEW — a synthesized merge of A + B: fast-forward (no conflicts)
//      vs a deterministic conflict set, with a ghost merge commit drawn on the
//      tree. Ported from gitMerge().
//
// DATA: Thread has a real CoW versioning layer (branches + commits). This panel
// prefers REAL branches/commits when supplied via props; otherwise it falls back
// to the prototype's deterministic-from-graph presentation so the design's branch
// view is always present (the user wants to SEE these views even on a graph that
// hasn't been branched yet).
//
// Colours come from --v5-* design tokens (see thread-v5.css) — never hard-coded.
// This component is PURE on its props (no fetch, no global state).

import { useCallback, useMemo, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Public API — REAL branch/commit data shape (matches Thread's CoW versioning).
// All fields optional/loose so a caller can pass whatever the versioning API
// returns; the panel normalises and lays out lanes itself.
// ─────────────────────────────────────────────────────────────────────────────

/** A real branch from the CoW versioning layer. */
export interface BranchInput {
  /** Branch name / id (e.g. "main", "high-Re"). */
  id: string;
  /** Optional explicit lane (0 = trunk). Assigned automatically if omitted. */
  lane?: number;
  /** Whether this branch has been merged back into the trunk. */
  merged?: boolean;
  /** Optional explicit tone token name (one of the v5 accent tokens). */
  tone?: BranchTone;
}

/** A real commit from the CoW versioning layer. */
export interface CommitInput {
  /** Short commit hash (the dot's identity). */
  hash: string;
  /** Branch id this commit lives on (maps to a lane). */
  branch?: string;
  /** Explicit lane override (else derived from `branch`). */
  lane?: number;
  /** Sequence column (monotonic along the branch). Derived if omitted. */
  seq?: number;
  /** Parent commit hashes (≥2 ⇒ a merge commit). */
  parents?: string[];
  /** Human commit message. */
  name?: string;
  /** Display date (e.g. "May 16"). */
  date?: string;
  /** Marks the current HEAD (ringed). */
  current?: boolean;
  /** Optional annotation chip (e.g. "only in coarse"). */
  tag?: string;
}

/** Minimal node shape — only `id` is needed for the deterministic fallback. */
export interface BranchPanelNode {
  id: string;
  [key: string]: unknown;
}

export interface DagBranchPanelProps {
  /**
   * Real branches from the versioning layer. When omitted (or empty) the panel
   * derives a deterministic branch graph from `nodes`/`rootId` so the design's
   * branch view is always present.
   */
  branches?: BranchInput[];
  /** Real commits from the versioning layer. Paired with `branches`. */
  commits?: CommitInput[];
  /**
   * The provenance nodes (used only for the deterministic fallback + the panel
   * title — which artifact's history are we looking at).
   */
  nodes?: BranchPanelNode[];
  /** The currently-selected artifact id (titles the history; seeds the fallback). */
  rootId?: string | null;
  /** Optional pre-picked compare commits (controlled). */
  compareA?: string | null;
  compareB?: string | null;
  /** Fires when the compare selection changes (A, B). Controlled when present. */
  onCompareChange?: (a: string | null, b: string | null) => void;
  /** Optional class on the root for layout. */
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tones — map the prototype's lane colours onto defined --v5-* tokens.
// (The prototype used --fc-ink / --fc-info / --fc-warning; here: ink / blue / amber.)
// ─────────────────────────────────────────────────────────────────────────────

export type BranchTone = "ink" | "blue" | "amber" | "primary";

const TONE_VAR: Record<BranchTone, string> = {
  ink: "var(--v5-ink)",
  blue: "var(--v5-blue)",
  amber: "var(--v5-amber)",
  primary: "var(--v5-primary)",
};

/** Lane → tone cycle (trunk is ink; feature lanes cycle through accents). */
const LANE_TONES: BranchTone[] = ["ink", "blue", "amber", "primary"];

// ─────────────────────────────────────────────────────────────────────────────
// Normalised model — what the renderer consumes regardless of real-vs-fallback.
// ─────────────────────────────────────────────────────────────────────────────

interface Branch {
  id: string;
  lane: number;
  merged: boolean;
  tone: BranchTone;
}

interface Commit {
  hash: string;
  lane: number;
  seq: number;
  parents: string[];
  name: string;
  date: string;
  current: boolean;
  tag?: string;
}

interface GitTree {
  branches: Branch[];
  commits: Commit[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Deterministic hash — the prototype's FNV-1a, so the fallback graph is stable
// per artifact id (and matches `hashFor` semantics used elsewhere in the design).
// ─────────────────────────────────────────────────────────────────────────────

export function hashFor(id: string): string {
  let h = 0x811c9dc5;
  const s = `thread:${id}`;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `0000000${(h >>> 0).toString(16)}`.slice(-7);
}

// ─────────────────────────────────────────────────────────────────────────────
// The deterministic prototype branch graph (used when no real branches given).
// This is the exact topology from the prototype's gitTree(): main + two feature
// branches (one merged, one not), with a fast-forward chain on main and a merge
// commit closing the high-Re branch. We derive the hashes from the artifact id so
// every artifact shows a plausible, stable history.
// ─────────────────────────────────────────────────────────────────────────────

function deterministicTree(rootId: string): GitTree {
  // Six stable hashes seeded off the artifact id (so each artifact looks unique).
  const h = (n: number) => hashFor(`${rootId}#${n}`);
  const [c0, c1, c2, c3, c4, c5] = [h(0), h(1), h(2), h(3), h(4), h(5)];

  const branches: Branch[] = [
    { id: "main", lane: 0, merged: true, tone: "ink" },
    { id: "high-Re", lane: 1, merged: true, tone: "blue" },
    { id: "coarse", lane: 2, merged: false, tone: "amber" },
  ];
  const commits: Commit[] = [
    { hash: c0, lane: 0, seq: 0, parents: [], name: "initial fit", date: "May 3", current: false },
    { hash: c1, lane: 0, seq: 1, parents: [c0], name: "add transonic", date: "May 8", current: false },
    { hash: c2, lane: 2, seq: 2, parents: [c0], name: "coarse baseline", date: "May 6", current: false, tag: "only in coarse" },
    { hash: c3, lane: 1, seq: 2, parents: [c1], name: "high-Re sweep", date: "May 11", current: false },
    { hash: c4, lane: 1, seq: 3, parents: [c3], name: "refit α>12", date: "May 14", current: false },
    { hash: c5, lane: 0, seq: 4, parents: [c1, c4], name: "merge high-Re", date: "May 16", current: true },
  ];
  return { branches, commits };
}

// ─────────────────────────────────────────────────────────────────────────────
// Normalise real input → the renderer model. Assigns lanes/seq where missing.
// ─────────────────────────────────────────────────────────────────────────────

function normaliseTree(branchInputs: BranchInput[], commitInputs: CommitInput[]): GitTree {
  // Assign lanes to branches: explicit lane wins, else first-seen order.
  const laneOf = new Map<string, number>();
  let nextLane = 0;
  const branches: Branch[] = branchInputs.map((b) => {
    const lane = b.lane ?? laneOf.get(b.id) ?? nextLane;
    if (!laneOf.has(b.id)) {
      laneOf.set(b.id, lane);
      nextLane = Math.max(nextLane, lane + 1);
    }
    return {
      id: b.id,
      lane,
      merged: b.merged ?? false,
      tone: b.tone ?? LANE_TONES[lane % LANE_TONES.length]!,
    };
  });

  // Per-branch running sequence for commits missing an explicit seq.
  const seqCursor = new Map<number, number>();
  const commits: Commit[] = commitInputs.map((c, i) => {
    const lane = c.lane ?? (c.branch != null ? laneOf.get(c.branch) ?? 0 : 0);
    let seq = c.seq;
    if (seq == null) {
      seq = seqCursor.get(lane) ?? 0;
      seqCursor.set(lane, seq + 1);
    } else {
      seqCursor.set(lane, Math.max(seqCursor.get(lane) ?? 0, seq + 1));
    }
    return {
      hash: c.hash,
      lane,
      seq,
      parents: c.parents ?? [],
      name: c.name ?? `commit ${i + 1}`,
      date: c.date ?? "",
      current: c.current ?? false,
      tag: c.tag,
    };
  });

  return { branches, commits };
}

// ─────────────────────────────────────────────────────────────────────────────
// Lineage helpers over the commit graph (parent-walks). Ported from the
// prototype's gitAncestors / gitDiff / gitMerge.
// ─────────────────────────────────────────────────────────────────────────────

function commitMap(commits: Commit[]): Map<string, Commit> {
  const m = new Map<string, Commit>();
  for (const c of commits) m.set(c.hash, c);
  return m;
}

/** Exclusive ancestor set of a commit hash (its lineage, not including itself). */
export function gitAncestors(commits: Commit[], hash: string): Set<string> {
  const byHash = commitMap(commits);
  const seen = new Set<string>();
  const stack = [hash];
  while (stack.length) {
    const h = stack.pop()!;
    const c = byHash.get(h);
    if (!c) continue;
    for (const p of c.parents) {
      if (!seen.has(p)) {
        seen.add(p);
        stack.push(p);
      }
    }
  }
  return seen;
}

export interface GitDiff {
  /** Plain-English relation between A and B. */
  rel: string;
  /** "compatible" (one contains the other) vs "needs merge" (diverged). */
  compat: "compatible" | "needs merge";
  shared: number;
  aOnly: number;
  bOnly: number;
  /** The nearest shared ancestor (the merge base). */
  base: string;
  baseName: string;
  aName: string;
  bName: string;
}

/** Compare two commits: are they on the same line or diverged? Ported from gitDiff. */
export function gitDiff(commits: Commit[], a: string | null, b: string | null): GitDiff | null {
  if (!a || !b) return null;
  const byHash = commitMap(commits);
  const ancA = new Set<string>([a, ...gitAncestors(commits, a)]);
  const ancB = new Set<string>([b, ...gitAncestors(commits, b)]);
  const shared = [...ancA].filter((h) => ancB.has(h));
  const aOnly = [...ancA].filter((h) => !ancB.has(h));
  const bOnly = [...ancB].filter((h) => !ancA.has(h));

  let rel: string;
  let compat: GitDiff["compat"];
  if (a === b) {
    rel = "same commit";
    compat = "compatible";
  } else if (ancB.has(a)) {
    rel = "B builds on A — A is an ancestor of B";
    compat = "compatible";
  } else if (ancA.has(b)) {
    rel = "A builds on B — B is an ancestor of A";
    compat = "compatible";
  } else {
    rel = "diverged — neither contains the other";
    compat = "needs merge";
  }

  const base = shared
    .map((h) => byHash.get(h)!)
    .filter(Boolean)
    .sort((x, y) => y.seq - x.seq)[0];

  return {
    rel,
    compat,
    shared: shared.length,
    aOnly: aOnly.length,
    bOnly: bOnly.length,
    base: base ? base.hash : "—",
    baseName: base ? base.name : "—",
    aName: byHash.get(a)?.name ?? a,
    bName: byHash.get(b)?.name ?? b,
  };
}

export interface GitMerge {
  /** Fast-forward (compatible) — no conflicts to resolve. */
  ff: boolean;
  /** Total commits the merged history would span. */
  span: number;
  /** Deterministic conflict set (artifacts touched on both sides since the base). */
  conflicts: string[];
  nConf: number;
  /** The synthesized merge commit hash. */
  mergeHash: string;
  /** One-line verdict for the readout. */
  verdict: string;
}

const CONFLICT_POOL = [
  "drag-polar fit",
  "C_m,α derivative",
  "reference area",
  "transition trip",
  "damping model",
];

/** Synthesize the merge of two commits. Ported from the prototype's gitMerge. */
export function gitMerge(commits: Commit[], a: string | null, b: string | null): GitMerge | null {
  const d = gitDiff(commits, a, b);
  if (!d || !a || !b) return null;
  const ff = d.compat === "compatible";
  const seed = [...a, ...b].reduce((s, ch) => s + ch.charCodeAt(0), 0);
  const nConf = ff ? 0 : 1 + (seed % 2);
  const conflicts = ff
    ? []
    : Array.from({ length: nConf }, (_, i) => CONFLICT_POOL[(seed + i * 7) % CONFLICT_POOL.length]!);
  return {
    ff,
    span: d.shared + d.aOnly + d.bOnly + 1,
    conflicts,
    nConf,
    mergeHash: hashFor(`${a}+${b}`),
    verdict: ff ? "fast-forward — no conflicts" : `${nConf} conflict${nConf > 1 ? "s" : ""} to resolve`,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Geometry — lay the lanes/commits out in a viewBox. Ported from gitView's X/laneY.
// ─────────────────────────────────────────────────────────────────────────────

const PANEL_W = 600;
const COL_W = 94; // px between sequence columns
const COL_X0 = 60; // left margin of seq 0
const LANE_H = 86; // px between lanes
const LANE_Y0 = 40; // top margin of lane 0

const seqX = (seq: number): number => COL_X0 + seq * COL_W;
const laneY = (lane: number): number => LANE_Y0 + lane * LANE_H;

interface CommitNode extends Commit {
  x: number;
  y: number;
  tone: BranchTone;
}

interface EdgePath {
  d: string;
  tone: BranchTone;
  dashed: boolean;
  key: string;
}

interface LayoutModel {
  width: number;
  height: number;
  commits: CommitNode[];
  edges: EdgePath[];
  branches: Branch[];
  byHash: Map<string, CommitNode>;
}

function layoutTree(tree: GitTree): LayoutModel {
  const toneByLane = new Map<number, BranchTone>();
  for (const b of tree.branches) toneByLane.set(b.lane, b.tone);

  const commits: CommitNode[] = tree.commits.map((c) => ({
    ...c,
    x: seqX(c.seq),
    y: laneY(c.lane),
    tone: toneByLane.get(c.lane) ?? "ink",
  }));
  const byHash = new Map<string, CommitNode>();
  for (const c of commits) byHash.set(c.hash, c);

  const mergedByLane = new Map<number, boolean>();
  for (const b of tree.branches) mergedByLane.set(b.lane, b.merged);

  const edges: EdgePath[] = [];
  for (const c of commits) {
    c.parents.forEach((ph, pi) => {
      const p = byHash.get(ph);
      if (!p) return;
      const d =
        p.y === c.y
          ? `M${p.x},${p.y} L${c.x},${c.y}`
          : `M${p.x},${p.y} C${(p.x + c.x) / 2},${p.y} ${(p.x + c.x) / 2},${c.y} ${c.x},${c.y}`;
      edges.push({
        d,
        tone: c.tone,
        dashed: !(mergedByLane.get(c.lane) ?? true),
        key: `${ph}->${c.hash}-${pi}`,
      });
    });
  }

  const maxSeq = commits.reduce((m, c) => Math.max(m, c.seq), 0);
  const maxLane = tree.branches.reduce((m, b) => Math.max(m, b.lane), 0);
  return {
    width: Math.max(PANEL_W, seqX(maxSeq) + 140),
    height: laneY(maxLane) + 70,
    commits,
    edges,
    branches: tree.branches,
    byHash,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function DagBranchPanel({
  branches,
  commits,
  nodes,
  rootId,
  compareA,
  compareB,
  onCompareChange,
  className,
}: DagBranchPanelProps) {
  // Build the normalised tree: REAL data when supplied, else the deterministic
  // prototype graph keyed off the selected artifact (always present).
  const usingReal = !!(branches && branches.length && commits && commits.length);
  const tree = useMemo<GitTree>(() => {
    if (usingReal) return normaliseTree(branches!, commits!);
    return deterministicTree(rootId || nodes?.[0]?.id || "artifact");
  }, [usingReal, branches, commits, rootId, nodes]);

  const layout = useMemo(() => layoutTree(tree), [tree]);

  // ── Compare selection (controlled when onCompareChange is given). ──
  const [internalA, setInternalA] = useState<string | null>(null);
  const [internalB, setInternalB] = useState<string | null>(null);
  const a = compareA !== undefined ? compareA : internalA;
  const b = compareB !== undefined ? compareB : internalB;

  const setCompare = useCallback(
    (na: string | null, nb: string | null) => {
      if (compareA === undefined) setInternalA(na);
      if (compareB === undefined) setInternalB(nb);
      onCompareChange?.(na, nb);
    },
    [compareA, compareB, onCompareChange],
  );

  // Picking semantics (ported from gitView.onSelect): first click sets A, second
  // sets B, clicking A again clears it, a third click restarts the pair.
  const pickCommit = useCallback(
    (hash: string) => {
      if (!a || (a && b)) {
        setCompare(hash, null);
        setMergePreview(false);
      } else if (hash === a) {
        setCompare(null, null);
        setMergePreview(false);
      } else {
        setCompare(a, hash);
        setMergePreview(false);
      }
    },
    [a, b, setCompare],
  );

  const clearCompare = useCallback(() => {
    setCompare(null, null);
    setMergePreview(false);
  }, [setCompare]);

  // ── Merge preview toggle. ──
  const [mergePreview, setMergePreview] = useState(false);

  const diff = useMemo(() => gitDiff(tree.commits, a, b), [tree.commits, a, b]);
  const merge = useMemo(() => gitMerge(tree.commits, a, b), [tree.commits, a, b]);
  const hasPair = !!(a && b);

  // ── Synthesized merge ghost commit + its edges (only when previewing a pair). ──
  const mergeGhost = useMemo(() => {
    if (!mergePreview || !hasPair || !merge) return null;
    const pa = layout.byHash.get(a!);
    const pb = layout.byHash.get(b!);
    if (!pa || !pb) return null;
    const mx = Math.max(pa.x, pb.x) + COL_W;
    const my = LANE_Y0;
    const tone: BranchTone = merge.ff ? "primary" : "amber";
    const edges = [pa, pb].map((p, i) => ({
      key: `merge-${i}`,
      d:
        p.y === my
          ? `M${p.x},${p.y} L${mx},${my}`
          : `M${p.x},${p.y} C${(p.x + mx) / 2},${p.y} ${(p.x + mx) / 2},${my} ${mx},${my}`,
      tone,
    }));
    return {
      x: mx,
      y: my,
      tone,
      hash: merge.mergeHash,
      label: merge.ff ? "merge (ff)" : "merge ⚠",
      edges,
    };
  }, [mergePreview, hasPair, merge, layout.byHash, a, b]);

  const width = Math.max(layout.width, mergeGhost ? mergeGhost.x + 90 : 0);

  const title = useMemo(() => {
    if (usingReal) return "branches & commits";
    const id = rootId || nodes?.[0]?.id;
    return id ? `${id} · branch history` : "branch history";
  }, [usingReal, rootId, nodes]);

  return (
    <div
      className={`v5-dag-branch${className ? ` ${className}` : ""}`}
      data-testid="v5-dag-branch"
      data-source={usingReal ? "real" : "synthetic"}
    >
      {/* ── Header: title + compare/merge controls. ── */}
      <div className="v5-dag-branch-head">
        <span className="v5-dag-branch-title">{title}</span>
        <span className="v5-dag-branch-actions">
          {hasPair ? (
            <button
              type="button"
              className={`v5-dag-branch-btn${mergePreview ? " is-active" : ""}`}
              data-testid="v5-dag-branch-merge-btn"
              aria-pressed={mergePreview}
              onClick={() => setMergePreview((m) => !m)}
            >
              {mergePreview ? "hide merge preview" : "preview merge →"}
            </button>
          ) : null}
          {a || b ? (
            <button type="button" className="v5-dag-branch-btn" onClick={clearCompare}>
              clear
            </button>
          ) : null}
        </span>
      </div>

      {/* ── The git/commit graph. ── */}
      <div className="v5-dag-branch-graph-wrap">
        <svg
          className="v5-dag-branch-svg"
          viewBox={`0 0 ${width} ${layout.height}`}
          preserveAspectRatio="xMidYMid meet"
          role="group"
          aria-label="Branch and commit graph"
          data-testid="v5-dag-branch-graph"
        >
          {/* Lane guides + labels. */}
          {layout.branches.map((br) => (
            <g key={`lane-${br.id}`}>
              <line
                className="v5-dag-branch-lane"
                x1={0}
                y1={laneY(br.lane)}
                x2={width}
                y2={laneY(br.lane)}
                stroke="var(--v5-line)"
                strokeWidth={1}
                strokeDasharray="2 5"
              />
              <text
                className="v5-dag-branch-lane-label"
                x={6}
                y={laneY(br.lane) - 12}
                style={{ fill: TONE_VAR[br.tone], fontWeight: 600 }}
              >
                {br.id}
              </text>
              <text
                className="v5-dag-branch-lane-note"
                x={6}
                y={laneY(br.lane) - 1}
                style={{ fill: br.merged ? "var(--v5-ink-4)" : "var(--v5-amber)" }}
              >
                {br.merged ? "merged" : "not in main"}
              </text>
            </g>
          ))}

          {/* Parent edges. */}
          {layout.edges.map((e) => (
            <path
              key={e.key}
              className="v5-dag-branch-edge"
              d={e.d}
              fill="none"
              stroke={TONE_VAR[e.tone]}
              strokeWidth={2}
              strokeDasharray={e.dashed ? "5 5" : "none"}
            />
          ))}

          {/* Synthesized merge edges + ghost commit (preview). */}
          {mergeGhost ? (
            <>
              {mergeGhost.edges.map((e) => (
                <path
                  key={e.key}
                  className="v5-dag-branch-edge is-merge"
                  d={e.d}
                  fill="none"
                  stroke={TONE_VAR[mergeGhost.tone]}
                  strokeWidth={2}
                  strokeDasharray="3 4"
                />
              ))}
              <circle
                className="v5-dag-branch-merge-dot"
                cx={mergeGhost.x}
                cy={mergeGhost.y}
                r={11}
                fill="var(--v5-surface)"
                stroke={TONE_VAR[mergeGhost.tone]}
                strokeWidth={2}
              />
              <text
                className="v5-dag-branch-merge-label"
                x={mergeGhost.x}
                y={mergeGhost.y - 16}
                textAnchor="middle"
                style={{ fill: TONE_VAR[mergeGhost.tone], fontWeight: 600 }}
              >
                {mergeGhost.label}
              </text>
            </>
          ) : null}

          {/* Commit dots. */}
          {layout.commits.map((c) => {
            const isA = c.hash === a;
            const isB = c.hash === b;
            const r = c.current ? 13 : 9.5;
            const fill =
              isA || isB ? TONE_VAR[c.tone] : c.current ? TONE_VAR[c.tone] : "var(--v5-surface)";
            const ringTone = isA
              ? "var(--v5-blue)"
              : isB
                ? "var(--v5-amber)"
                : c.current
                  ? "var(--v5-primary-soft)"
                  : null;
            return (
              <g
                key={c.hash}
                className="v5-dag-branch-commit"
                role="button"
                tabIndex={0}
                aria-label={`${c.name} (${c.hash})`}
                aria-pressed={isA || isB}
                data-testid="v5-dag-branch-commit"
                data-hash={c.hash}
                data-pick={isA ? "a" : isB ? "b" : ""}
                data-current={c.current ? "true" : "false"}
                onClick={() => pickCommit(c.hash)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    pickCommit(c.hash);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {ringTone ? (
                  <circle cx={c.x} cy={c.y} r={r + 3.5} fill="none" stroke={ringTone} strokeWidth={3} />
                ) : null}
                <circle
                  cx={c.x}
                  cy={c.y}
                  r={r}
                  fill={fill}
                  stroke={TONE_VAR[c.tone]}
                  strokeWidth={2}
                />
                {isA || isB ? (
                  <text
                    x={c.x}
                    y={c.y + 4}
                    textAnchor="middle"
                    style={{ fill: "var(--v5-surface)", fontWeight: 700, fontSize: 11 }}
                  >
                    {isA ? "A" : "B"}
                  </text>
                ) : null}
                <text
                  className="v5-dag-branch-commit-name"
                  x={c.x}
                  y={c.y + r + 15}
                  textAnchor="middle"
                  style={{
                    fill: c.current ? "var(--v5-ink)" : "var(--v5-ink-3)",
                    fontWeight: c.current ? 600 : 500,
                  }}
                >
                  {c.name}
                </text>
                <text
                  className="v5-dag-branch-commit-date"
                  x={c.x}
                  y={c.y + r + 28}
                  textAnchor="middle"
                  style={{ fill: "var(--v5-ink-4)" }}
                >
                  {c.hash}
                  {c.date ? ` · ${c.date}` : ""}
                </text>
                {c.tag ? (
                  <text
                    className="v5-dag-branch-commit-tag"
                    x={c.x}
                    y={c.y - r - 8}
                    textAnchor="middle"
                    style={{ fill: "var(--v5-amber)", fontStyle: "italic" }}
                  >
                    {c.tag}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>

      {/* ── Compare readout. ── */}
      <div className="v5-dag-branch-compare" data-testid="v5-dag-branch-compare">
        {!hasPair ? (
          <p className="v5-dag-branch-hint" data-testid="v5-dag-branch-hint">
            {a
              ? "commit A picked — click another commit to compare."
              : "click any two commits in the tree to diff their lineages."}
          </p>
        ) : diff ? (
          <>
            <div className="v5-dag-branch-diff-row">
              <span className="v5-dag-branch-diff-side">
                <span className="v5-dag-branch-pip is-a" aria-hidden="true" />A · {diff.aName}
              </span>
              <span className="v5-dag-branch-diff-side">
                <span className="v5-dag-branch-pip is-b" aria-hidden="true" />B · {diff.bName}
              </span>
            </div>
            <p
              className="v5-dag-branch-diff-rel"
              data-testid="v5-dag-branch-rel"
              style={{
                color: diff.compat === "compatible" ? "var(--v5-primary)" : "var(--v5-amber)",
              }}
            >
              {diff.rel}
            </p>
            <dl className="v5-dag-branch-diff-stats">
              <div>
                <dt>shared base</dt>
                <dd>
                  {diff.baseName} ({diff.base})
                </dd>
              </div>
              <div>
                <dt>only in A</dt>
                <dd data-testid="v5-dag-branch-aonly">{diff.aOnly}</dd>
              </div>
              <div>
                <dt>only in B</dt>
                <dd data-testid="v5-dag-branch-bonly">{diff.bOnly}</dd>
              </div>
              <div>
                <dt>shared</dt>
                <dd>{diff.shared}</dd>
              </div>
            </dl>

            {/* Merge preview readout (only when toggled on). */}
            {mergePreview && merge ? (
              <div
                className="v5-dag-branch-merge-readout"
                data-testid="v5-dag-branch-merge-readout"
                data-ff={merge.ff ? "true" : "false"}
              >
                <p
                  className="v5-dag-branch-merge-verdict"
                  data-testid="v5-dag-branch-merge-verdict"
                  style={{ color: merge.ff ? "var(--v5-primary)" : "var(--v5-amber)" }}
                >
                  {merge.ff ? "✓ " : "⚠ "}
                  {merge.verdict}
                </p>
                <p className="v5-dag-branch-merge-span">
                  merge {merge.mergeHash} · spans {merge.span} commits
                </p>
                {merge.conflicts.length ? (
                  <ul className="v5-dag-branch-conflicts" data-testid="v5-dag-branch-conflicts">
                    {merge.conflicts.map((cf) => (
                      <li key={cf}>{cf}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

export default DagBranchPanel;
